import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Post, Category } from '../contexts/AdminContext';
import { BlogPost, BlogCategory, mockPublishedPosts, mockCategories } from './blogData';
import { smsService } from './smsService';

// User interface
export interface User {
  id: string;
  name: string;
  username: string;
  email: string;
  mobile: string;
  password: string;
  role: 'admin' | 'editor' | 'user';
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

// Only one admin user
export let mockUsers: User[] = [
  {
    id: '1',
    name: 'Admin User',
    username: 'admin',
    email: 'admin@seoxpert.com',
    mobile: '9897785402',
    password: 'Uttu@2405',
    role: 'admin',
    status: 'active',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('adminToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Convert BlogPost to Post format for admin
const convertBlogPostToPost = (blogPost: BlogPost): Post => {
  const postCategories = blogPost.categories.map(catName => {
    const category = mockCategories.find(c => c.name === catName);
    return category ? {
      id: category.id,
      name: category.name,
      slug: category.slug,
      description: category.description,
      createdAt: category.createdAt,
      updatedAt: category.updatedAt,
    } : {
      id: '',
      name: catName,
      slug: catName.toLowerCase().replace(/\s+/g, '-'),
      description: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });

  return {
    id: blogPost.id,
    title: blogPost.title,
    slug: blogPost.slug,
    content: blogPost.content,
    excerpt: blogPost.excerpt || '',
    status: blogPost.status,
    categories: postCategories,
    metaTitle: blogPost.metaTitle || '',
    metaDescription: blogPost.metaDescription || '',
    metaRobots: blogPost.metaRobots as 'index,follow' | 'noindex,nofollow' | 'index,nofollow' | 'noindex,follow',
    focusKeyphrase: blogPost.focusKeyphrase || '',
    createdAt: blogPost.createdAt,
    updatedAt: blogPost.updatedAt,
    publishedAt: blogPost.publishedAt,
  };
};

// Convert Post to BlogPost format
const convertPostToBlogPost = (post: Post): BlogPost => ({
  id: post.id,
  title: post.title,
  slug: post.slug,
  content: post.content,
  excerpt: post.excerpt || '',
  status: post.status,
  categories: post.categories.map(cat => cat.name),
  metaTitle: post.metaTitle || '',
  metaDescription: post.metaDescription || '',
  metaRobots: post.metaRobots,
  focusKeyphrase: post.focusKeyphrase || '',
  createdAt: post.createdAt,
  updatedAt: post.updatedAt,
  publishedAt: post.publishedAt,
  author: 'Utkrsh Sharma',
  readTime: '5 min read',
});

// Convert BlogCategory to Category format for admin
const convertBlogCategoryToCategory = (blogCategory: BlogCategory): Category => ({
  id: blogCategory.id,
  name: blogCategory.name,
  slug: blogCategory.slug,
  description: blogCategory.description,
  createdAt: blogCategory.createdAt,
  updatedAt: blogCategory.updatedAt,
});

// Convert Category to BlogCategory format
const convertCategoryToBlogCategory = (category: Category): BlogCategory => ({
  id: category.id,
  name: category.name,
  slug: category.slug,
  description: category.description || '',
  createdAt: category.createdAt,
  updatedAt: category.updatedAt,
});

// Posts API with shared data
export const postsAPI = {
  getPosts: async (): Promise<Post[]> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockPublishedPosts.map(convertBlogPostToPost);
  },

  getPost: async (id: string): Promise<Post> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const post = mockPublishedPosts.find(p => p.id === id);
    if (!post) throw new Error('Post not found');
    return convertBlogPostToPost(post);
  },

  createPost: async (postData: Omit<Post, 'id' | 'createdAt' | 'updatedAt'>): Promise<Post> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const newBlogPost: BlogPost = {
      ...convertPostToBlogPost(postData as Post),
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    mockPublishedPosts.push(newBlogPost);
    return convertBlogPostToPost(newBlogPost);
  },

  updatePost: async (id: string, postData: Partial<Post>): Promise<Post> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const index = mockPublishedPosts.findIndex(p => p.id === id);
    if (index === -1) throw new Error('Post not found');
    
    const updatedBlogPost: BlogPost = {
      ...mockPublishedPosts[index],
      ...convertPostToBlogPost(postData as Post),
      updatedAt: new Date().toISOString(),
    };
    
    mockPublishedPosts[index] = updatedBlogPost;
    return convertBlogPostToPost(updatedBlogPost);
  },

  deletePost: async (id: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const index = mockPublishedPosts.findIndex(p => p.id === id);
    if (index !== -1) {
      mockPublishedPosts.splice(index, 1);
    }
  },

  searchPosts: async (query: string): Promise<Post[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const filteredPosts = mockPublishedPosts.filter(post => 
      post.title.toLowerCase().includes(query.toLowerCase()) ||
      post.content.toLowerCase().includes(query.toLowerCase())
    );
    return filteredPosts.map(convertBlogPostToPost);
  },
};

// Categories API with shared data
export const categoriesAPI = {
  getCategories: async (): Promise<Category[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockCategories.map(convertBlogCategoryToCategory);
  },

  getCategory: async (id: string): Promise<Category> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    const category = mockCategories.find(c => c.id === id);
    if (!category) throw new Error('Category not found');
    return convertBlogCategoryToCategory(category);
  },

  createCategory: async (categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<Category> => {
    await new Promise(resolve => setTimeout(resolve, 400));
    const newBlogCategory: BlogCategory = {
      ...convertCategoryToBlogCategory(categoryData as Category),
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    mockCategories.push(newBlogCategory);
    return convertBlogCategoryToCategory(newBlogCategory);
  },

  updateCategory: async (id: string, categoryData: Partial<Category>): Promise<Category> => {
    await new Promise(resolve => setTimeout(resolve, 400));
    const index = mockCategories.findIndex(c => c.id === id);
    if (index === -1) throw new Error('Category not found');
    
    const updatedBlogCategory: BlogCategory = {
      ...mockCategories[index],
      ...convertCategoryToBlogCategory(categoryData as Category),
      updatedAt: new Date().toISOString(),
    };
    
    mockCategories[index] = updatedBlogCategory;
    return convertBlogCategoryToCategory(updatedBlogCategory);
  },

  deleteCategory: async (id: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const index = mockCategories.findIndex(c => c.id === id);
    if (index !== -1) {
      mockCategories.splice(index, 1);
    }
  },
};

// Media API with mock data
export const mediaAPI = {
  uploadImage: async (file: File): Promise<{ url: string; altText: string }> => {
    try {
      console.log('Starting image upload for file:', file.name, 'Size:', file.size);
      
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }
      
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        throw new Error('File size must be less than 5MB');
      }
      
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create object URL for preview
      const url = URL.createObjectURL(file);
      
      console.log('Image upload successful:', url);
      
      return {
        url,
        altText: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
      };
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  },
};

// Auth API with mock authentication
export const authAPI = {
  login: async (credentials: { username: string; password: string }): Promise<{ token: string; user: any }> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    // Only allow login for the real admin
    const adminUser = mockUsers.find(u => u.username === credentials.username);
    if (adminUser && adminUser.password === credentials.password) {
      const token = 'mock-jwt-token-' + Date.now();
      const user = {
        id: adminUser.id,
        name: adminUser.name,
        email: adminUser.email,
        mobile: adminUser.mobile,
        role: adminUser.role,
      };
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminUser', JSON.stringify(user));
      return { token, user };
    } else {
      throw new Error('Invalid credentials');
    }
  },

  sendOTP: async (mobile: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Validate mobile number
    if (!mobile || mobile.length !== 10 || !/^[0-9]+$/.test(mobile)) {
      throw new Error('Invalid mobile number');
    }
    try {
      // Only use Twilio (no demo fallback)
      await smsService.sendOTP(mobile, { forceProvider: 'twilio' });
      console.log(`OTP sent to ${mobile} via Twilio Verify`);
    } catch (error: any) {
      console.error('OTP sending failed:', error);
      throw new Error(error.message || 'Failed to send OTP');
    }
  },

  verifyOTP: async (mobile: string, otp: string): Promise<{ token: string; user: any }> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    // Only use Twilio (no demo fallback)
    const isValid = await smsService.verifyOTP(mobile, otp, { forceProvider: 'twilio' });
    if (!isValid) {
      throw new Error('Invalid or expired OTP');
    }
    const token = 'mock-jwt-token-otp-' + Date.now();
    const user = {
      id: '1',
      name: 'Admin User',
      email: 'admin@seoxpert.com',
      mobile: mobile,
      role: 'admin',
    };
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminUser', JSON.stringify(user));
    return { token, user };
  },

  logout: async (): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('demoOTP');
    localStorage.removeItem('demoMobile');
  },

  getProfile: async (): Promise<any> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const userStr = localStorage.getItem('adminUser');
    if (!userStr) throw new Error('Not authenticated');
    return { user: JSON.parse(userStr) };
  },
};

// User management API
export const usersAPI = {
  getUsers: async (): Promise<User[]> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockUsers;
  },

  getUser: async (id: string): Promise<User> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const user = mockUsers.find(u => u.id === id);
    if (!user) throw new Error('User not found');
    return user;
  },

  createUser: async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if username or email already exists
    const existingUser = mockUsers.find(u => 
      u.username === userData.username || u.email === userData.email
    );
    if (existingUser) {
      throw new Error('Username or email already exists');
    }

    const newUser: User = {
      ...userData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    mockUsers.push(newUser);
    return newUser;
  },

  updateUser: async (id: string, userData: Partial<User>): Promise<User> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const index = mockUsers.findIndex(u => u.id === id);
    if (index === -1) throw new Error('User not found');
    
    // Check if username or email already exists (excluding current user)
    if (userData.username || userData.email) {
      const existingUser = mockUsers.find(u => 
        u.id !== id && (
          (userData.username && u.username === userData.username) ||
          (userData.email && u.email === userData.email)
        )
      );
      if (existingUser) {
        throw new Error('Username or email already exists');
      }
    }
    
    const updatedUser: User = {
      ...mockUsers[index],
      ...userData,
      updatedAt: new Date().toISOString(),
    };
    
    mockUsers[index] = updatedUser;
    return updatedUser;
  },

  deleteUser: async (id: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const index = mockUsers.findIndex(u => u.id === id);
    if (index !== -1) {
      mockUsers.splice(index, 1);
    }
  },

  changePassword: async (id: string, currentPassword: string, newPassword: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const user = mockUsers.find(u => u.id === id);
    if (!user) throw new Error('User not found');
    
    // In a real app, you'd verify the current password hash
    if (user.password !== currentPassword) {
      throw new Error('Current password is incorrect');
    }
    
    user.password = newPassword;
    user.updatedAt = new Date().toISOString();
  },

  changeMobile: async (id: string, newMobile: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const user = mockUsers.find(u => u.id === id);
    if (!user) throw new Error('User not found');
    
    // Check if mobile number already exists
    const existingUser = mockUsers.find(u => u.id !== id && u.mobile === newMobile);
    if (existingUser) {
      throw new Error('Mobile number already exists');
    }
    
    user.mobile = newMobile;
    user.updatedAt = new Date().toISOString();
  },

  searchUsers: async (query: string): Promise<User[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const filteredUsers = mockUsers.filter(user => 
      user.name.toLowerCase().includes(query.toLowerCase()) ||
      user.username.toLowerCase().includes(query.toLowerCase()) ||
      user.email.toLowerCase().includes(query.toLowerCase()) ||
      user.mobile.includes(query)
    );
    return filteredUsers;
  },
};

export default api; 